import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { 
  Play, 
  Edit, 
  Trash2, 
  Plus, 
  ArrowLeft, 
  Eye, 
  Check, 
  X, 
  AlertCircle,
  HelpCircle,
  Code,
  Sparkles,
  Zap,
  CheckCircle2,
  User,
  Layout,
  RotateCw,
  ChevronDown,
  ChevronUp,
  Bot,
  Gavel,
  Target,
  Gauge,
  FileText
} from 'lucide-react';
import { faVials } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import {
  PageContainer,
  Card,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  fadeIn
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { MultiSelectDropdownSearch } from '../components/common/MultiSelectSearch';
import Pagination from '../components/common/Pagination';
import { 
  createEvalPack, 
  updateEvalPack, 
  deleteEvalPack, 
  runEvalPack, 
  getEvalTargets,
  getEvalReports,
  getEvalPacksPaginated,
  getEvalPacksCount
} from '../services/aiEvalService';

// --- Styled Components ---

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TabGroup = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
`;

const TabButton = styled.button`
  background: ${props => props.active ? '#ffffff' : 'transparent'};
  color: ${props => props.active ? 'var(--primary-600)' : '#64748b'};
  border: none;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.05)' : 'none'};

  &:hover {
    color: var(--primary-600);
  }
`;

// Stepper Components
const StepperContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0 1rem;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${props => props.active ? 'var(--primary-500)' : props.done ? '#22c55e' : '#e2e8f0'};
  color: ${props => props.active || props.done ? '#ffffff' : '#64748b'};
  border: 2px solid ${props => props.active ? 'var(--primary-500)' : props.done ? '#22c55e' : '#cbd5e1'};
  transition: all 0.3s ease;
`;

const StepLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${props => props.active ? '#1e293b' : '#64748b'};
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${props => props.done ? '#22c55e' : '#e2e8f0'};
  margin: 0 16px;
  transition: background 0.3s ease;
`;

// Segmented Pickers
const SegmentedControl = styled.div`
  display: flex;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
  width: 100%;
`;

const SegmentOption = styled.div`
  flex: 1;
  text-align: center;
  padding: 10px 8px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.active ? '#ffffff' : '#64748b'};
  background: ${props => props.active ? 'var(--primary-500)' : 'transparent'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.4 : 1};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;

  span.sub {
    font-size: 0.7rem;
    font-weight: 400;
    opacity: 0.85;
  }
`;

// Form Fields
const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
  }

  input[type=text], input[type=number], select, textarea {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 0.9rem;
    color: #1e293b;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
      border-color: var(--primary-500);
      box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const SettingsPanel = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 1.5rem;
`;

const TestCaseCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 12px;
  position: relative;
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    
    span.num {
      font-size: 0.75rem;
      font-weight: 600;
      color: #94a3b8;
      letter-spacing: 0.5px;
    }
  }
`;

const ToolCallRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  
  span.index {
    font-family: monospace;
    font-size: 0.8rem;
    color: #94a3b8;
    width: 16px;
  }
  
  input {
    flex: 1;
    padding: 6px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 0.85rem;
    outline: none;
  }
`;

// Modal / Run Styles
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 460px;
  max-width: 90vw;
  padding: 24px;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
  animation: slideUp 0.25s ease-out;

  @keyframes slideUp {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h3 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }
`;

const ResultRing = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-weight: 700;
  font-size: 0.95rem;
  background: ${props => `conic-gradient(${props.color} ${props.percent}%, #f1f5f9 0)`};
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.05);

  &::before {
    content: '';
    position: absolute;
    inset: 8px;
    border-radius: 50%;
    background: #ffffff;
  }

  span {
    position: relative;
    z-index: 2;
  }
`;

const JSONPreview = styled.pre`
  background: #0f172a;
  color: #94a3b8;
  padding: 16px;
  border-radius: 10px;
  font-family: Menlo, Monaco, Consolas, Courier New, monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #1e293b;

  .key { color: #818cf8; }
  .string { color: #34d399; }
  .number { color: #fbbf24; }
  .boolean { color: #38bdf8; }
`;

// Toast Notification
const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #1e293b;
  color: #ffffff;
  border-radius: 8px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
  z-index: 3000;
  font-size: 0.85rem;
  animation: popIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @keyframes popIn {
    from { transform: scale(0.9) translateY(12px); opacity: 0; }
    to { transform: scale(1) translateY(0); opacity: 1; }
  }
`;

// Switch toggle
const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: #cbd5e1;
    border-radius: 20px;
    transition: .2s;

    &::before {
      content: "";
      position: absolute;
      height: 16px;
      width: 16px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      border-radius: 50%;
      transition: .2s;
    }
  }

  input:checked + span {
    background-color: var(--primary-500);
  }

  input:checked + span::before {
    transform: translateX(18px);
  }
`;

const WizardModal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const WizardModalContent = styled(motion.div)`
  width: 1100px;
  max-width: 95%;
  background: #ffffff;
  border-radius: 16px;
  padding: 2rem;
  height: 90vh;            
  display: flex;            
  flex-direction: column; 
  overflow: hidden; 
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const WizardModalTopBar = styled.div`
 display: flex; 
 flex-direction: column;
 flex-shrink: 0;
 background: #ffffff;
 z-index: 2;
 border-bottom: 1px solid #e2e8f0;
 padding-bottom: 1rem;
`;

const WizardModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const WizardModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const WizardCloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: var(--text-secondary);
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const WizardStepIndicator = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  position: relative;
  padding: 0 2rem;
  
  &:after {
    content: '';
    position: absolute;
    top: 16px;
    left: 4rem;
    right: 4rem;
    height: 2px;
    background: rgba(0, 102, 255, 0.1);
    z-index: 0;
  }
`;

const WizardStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  z-index: 1;
  width: 150px;
`;

const WizardStepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.active ? 'var(--gradient-primary)' : props.completed ? 'var(--primary)' : 'rgba(0, 102, 255, 0.1)'};
  color: ${props => props.active || props.completed ? '#ffffff' : 'var(--text-secondary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? '0 0 15px rgba(0, 102, 255, 0.3)' : 'none'};
`;

const WizardStepLabel = styled.div`
  font-size: 0.8rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-weight: ${props => props.active ? '600' : '400'};
  text-align: center;
  transition: all 0.3s ease;
  white-space: nowrap;
`;

const WizardModalSection = styled.div`
  flex: 1;               
  overflow-y: auto;          
  padding: 2rem 1rem;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
`;

const WizardModalFooter = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem; 
  max-height: 70px;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
`;

const InfoBox = styled.div`
  background: rgba(0, 102, 255, 0.05);
  border-left: 3px solid var(--primary);
  padding: 1rem;
  border-radius: 0 8px 8px 0;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const WizardLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.85rem;
`;

const WizardInput = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
  
  &:disabled {
    background: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0;
    cursor: not-allowed;
  }
`;

const WizardSelect = styled.select`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230066FF' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const WizardTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 102, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
  }
  
  &:hover {
    border-color: rgba(0, 102, 255, 0.3);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  .label-group {
    display: flex;
    flex-direction: column;
    
    span.title {
      font-size: 0.85rem;
      font-weight: 600;
      color: #334155;
    }

    span.desc {
      font-size: 0.75rem;
      color: #64748b;
    }
  }
`;

// --- MAIN COMPONENT ---

const EvalPacks = () => {
  const navigate = useNavigate();
  
  const [packs, setPacks] = useState([]);
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewAll, setViewAll] = useState(false);
  const pageSize = 5;
  
  // Create / Edit Wizard State
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [editingPackId, setEditingPackId] = useState(null);
  const [wizardForm, setWizardForm] = useState(null);
  const [methodologyCases, setMethodologyCases] = useState({
    accuracy: [],
    agent_as_judge: [],
    reliability: [],
    performance: []
  });
  const [expandedPreviewCases, setExpandedPreviewCases] = useState({ 0: true });
  const [expandedStep2Cases, setExpandedStep2Cases] = useState({ 0: true });
  
  // Execution Modal State
  const [selectedPackForRun, setSelectedPackForRun] = useState(null);
  const [runEnv, setRunEnv] = useState('staging');
  const [runMode, setRunMode] = useState('sync');
  const [runProgress, setRunProgress] = useState(null);
  const [runResult, setRunResult] = useState(null);

  // Toast notifications
  const [toastMsg, setToastMsg] = useState(null);

  const [targets, setTargets] = useState({ agent: [], planner: [], pipeline: [] });

  // Fetch initial packs
  const fetchPacksData = useCallback(async () => {
    try {
      const filter = {};
      if (activeFilterTab !== 'all') {
        filter.target_type = activeFilterTab;
      }
      if (searchTerm) {
        filter.name = { ilike: searchTerm };
      }

      const count = await getEvalPacksCount(filter);
      setTotalCount(count);

      const skip = viewAll ? 0 : (currentPage - 1) * pageSize;
      const limit = viewAll ? 100000 : pageSize;

      const data = await getEvalPacksPaginated(filter, skip, limit);
      setPacks(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [activeFilterTab, searchTerm, currentPage, pageSize, viewAll]);

  useEffect(() => {
    fetchPacksData();
  }, [fetchPacksData]);

  useEffect(() => {
    const loadTargetsAndReports = async () => {
      const data = await getEvalTargets();
      if (data) {
        setTargets(data);
      }
      try {
        const reportData = await getEvalReports();
        setReports(reportData || []);
      } catch (err) {
        console.error(err);
      }
    };
    loadTargetsAndReports();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilterTab]);

  // Show toast notification
  const triggerToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };

  const getTargetName = (targetType, targetCode) => {
    if (!targetCode) return '—';
    const list = targets[targetType?.toLowerCase()] || [];
    const match = list.find(t => t.code === targetCode);
    if (match && match.name) {
      return match.name;
    }
    return targetCode;
  };

  const getPackIcon = (pack) => {
    const name = pack.name?.toLowerCase() || '';
    if (name === 'performance pipeline') {
      return { icon: <Gauge size={16} />, bg: '#f5f3ff', color: '#8b5cf6' };
    }
    if (name === 'accuracy pipeline') {
      return { icon: <Target size={16} />, bg: '#f0fdf4', color: '#22c55e' };
    }
    if (name === 'fetch_weather_info_performance') {
      return { icon: <Bot size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    if (name === 'fetch_weather_info_judge') {
      return { icon: <Gavel size={16} />, bg: '#fffbeb', color: '#d97706' };
    }
    if (name === 'fetch_weather_info') {
      return { icon: <Target size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    if (name === 'test') {
      return { icon: <FileText size={16} />, bg: '#f8fafc', color: '#64748b' };
    }
    // Generic fallback based on target/eval type
    if (pack.eval_type === 'performance') {
      return { icon: <Gauge size={16} />, bg: '#f5f3ff', color: '#8b5cf6' };
    }
    if (pack.eval_type === 'agent_as_judge') {
      return { icon: <Gavel size={16} />, bg: '#fffbeb', color: '#d97706' };
    }
    if (pack.target_type === 'agent') {
      return { icon: <Bot size={16} />, bg: '#eff6ff', color: '#3b82f6' };
    }
    return { icon: <Target size={16} />, bg: '#f0fdf4', color: '#22c55e' };
  };

  const getPackStatus = (packCode) => {
    const packReports = reports.filter(r => r.eval_pack_id === packCode);
    if (packReports.length === 0) {
      return 'No Runs';
    }
    const sorted = [...packReports].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
    return sorted[0].status;
  };

  const renderEvaluationTypeBadge = (evalType) => {
    const type = evalType?.toLowerCase();
    let bg = '#f0fdf4';
    let color = '#15803d';
    let border = '#dcfce7';
    let icon = <Target size={14} style={{ marginRight: '6px' }} />;
    let label = 'Accuracy';

    if (type === 'performance') {
      bg = '#f5f3ff';
      color = '#6d28d9';
      border = '#ede9fe';
      icon = <Gauge size={14} style={{ marginRight: '6px' }} />;
      label = 'Performance';
    } else if (type === 'agent_as_judge') {
      bg = '#fffbeb';
      color = '#b45309';
      border = '#fef3c7';
      icon = <Gavel size={14} style={{ marginRight: '6px' }} />;
      label = 'Agent-As-Judge';
    } else if (type === 'reliability') {
      bg = '#fff7ed';
      color = '#c2410c';
      border = '#ffedd5';
      icon = <Zap size={14} style={{ marginRight: '6px' }} />;
      label = 'Reliability';
    }

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.78rem',
        fontWeight: 600
      }}>
        {icon}
        {label}
      </span>
    );
  };

  const renderStatusBadge = (pack) => {
    const status = getPackStatus(pack.code)?.toLowerCase();
    let bg = '#f1f5f9';
    let color = '#475569';
    let border = '#e2e8f0';
    let dotColor = '#94a3b8';
    let labelText = 'No Runs';

    if (status === 'passed') {
      bg = '#f0fdf4';
      color = '#15803d';
      border = '#dcfce7';
      dotColor = '#22c55e';
      labelText = 'Passed';
    } else if (status === 'failed') {
      bg = '#fef2f2';
      color = '#b91c1c';
      border = '#fee2e2';
      dotColor = '#ef4444';
      labelText = 'Failed';
    } else if (status === 'running') {
      bg = '#fffbeb';
      color = '#b45309';
      border = '#fef3c7';
      dotColor = '#f59e0b';
      labelText = 'Running';
    } else {
      const packStatus = pack.status?.toLowerCase();
      if (packStatus === 'active') {
        bg = '#eff6ff';
        color = '#1d4ed8';
        border = '#dbeafe';
        dotColor = '#3b82f6';
        labelText = 'Active';
      } else if (packStatus === 'draft') {
        bg = '#f8fafc';
        color = '#64748b';
        border = '#f1f5f9';
        dotColor = '#94a3b8';
        labelText = 'Draft';
      }
    }

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        padding: '4px 10px',
        borderRadius: '8px',
        fontSize: '0.78rem',
        fontWeight: 600,
        gap: '6px'
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor }}></span>
        {labelText}
      </span>
    );
  };

  const formatLastModifiedDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateStr;
    }
  };

  // --- CRUD ACTIONS ---

  const handleDelete = async (e, id, code) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete evaluation pack "${code}"?`)) return;
    try {
      await deleteEvalPack(id);
      triggerToast(`Deleted evaluation pack ${code}`);
      fetchPacksData();
    } catch (err) {
      console.error(err);
    }
  };

  // Open creation wizard
  const initCreateWizard = () => {
    setEditingPackId(null);
    setMethodologyCases({
      accuracy: [],
      agent_as_judge: [],
      reliability: [],
      performance: []
    });
    setWizardForm({
      code: `EVAL-${uuid()}`,
      name: '',
      description: '',
      target_type: 'agent',
      target_code: targets.agent[0]?.code || '',
      eval_type: 'accuracy',
      pass_threshold: 8,
      settings: { num_iterations: 1, evaluator_model: 'gpt-4o', additional_guidelines: '' },
      test_case_list: [{ name: 'TEST CASE 1', input: '', expected_output: '', num_iterations: 1, threshold: 0.8, eval_type: 'accuracy', additional_guidelines: '' }]
    });
    setWizardStep(1);
    setIsWizardOpen(true);
  };

  // Open edit wizard
  const initEditWizard = (pack) => {
    setEditingPackId(pack.id);
    const deepCopy = JSON.parse(JSON.stringify(pack));
    if (deepCopy.test_case_list) {
      deepCopy.test_case_list = deepCopy.test_case_list.map((c, idx) => ({
        ...c,
        num_iterations: c.num_iterations !== undefined ? c.num_iterations : 1,
        threshold: c.threshold !== undefined ? c.threshold : 0.8,
        eval_type: c.eval_type || deepCopy.eval_type || 'accuracy'
      }));
    }
    const evalType = deepCopy.eval_type || 'accuracy';
    if (deepCopy.pass_threshold === undefined || deepCopy.pass_threshold === null) {
      deepCopy.pass_threshold = deepCopy.settings?.pass_score || (evalType === 'agent_as_judge' ? 7.5 : 8);
    }
    setMethodologyCases({
      accuracy: evalType === 'accuracy' ? deepCopy.test_case_list : [],
      agent_as_judge: evalType === 'agent_as_judge' ? deepCopy.test_case_list : [],
      reliability: evalType === 'reliability' ? deepCopy.test_case_list : [],
      performance: evalType === 'performance' ? deepCopy.test_case_list : []
    });
    setWizardForm(deepCopy);
    setWizardStep(1);
    setIsWizardOpen(true);
  };

  const closeWizardModal = () => {
    setIsWizardOpen(false);
  };

  // --- WIZARD FORM STEP HELPERS ---

  const updateWizardField = (field, val) => {
    setWizardForm(prev => ({
      ...prev,
      [field]: val
    }));
  };

  const updateWizardSettings = (field, val) => {
    setWizardForm(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [field]: val
      }
    }));
  };

  const setTargetType = (type) => {
    setWizardForm(prev => ({
      ...prev,
      target_type: type,
      target_code: targets[type]?.[0]?.code || ''
    }));
  };

  const handleEvalTypeChange = (type) => {
    let settings = {};
    let passThreshold = null;

    if (type === 'accuracy') {
      settings = { num_iterations: 1, evaluator_model: 'gpt-4o', additional_guidelines: '' };
      passThreshold = 8;
    } else if (type === 'agent_as_judge') {
      settings = { num_iterations: 1, evaluator_model: 'gpt-4o' };
      passThreshold = 7.5;
    } else if (type === 'reliability') {
      settings = { allow_additional_tool_calls: true, expected_call_order: false, num_iterations: 1 };
    } else if (type === 'performance') {
      settings = { num_iterations: 5, warmup_runs: 1, measure_memory: true, max_latency_ms: 4000, max_memory_mb: 512 };
    }

    setMethodologyCases(prev => {
      const oldType = wizardForm?.eval_type || 'accuracy';
      const updatedSlots = {
        ...prev,
        [oldType]: wizardForm?.test_case_list || []
      };

      let nextCases = updatedSlots[type] || [];
      if (nextCases.length === 0) {
        if (type === 'accuracy') {
          nextCases = [{ name: 'TEST CASE 1', input: '', expected_output: '', num_iterations: 1, threshold: 0.8, eval_type: 'accuracy', additional_guidelines: '' }];
        } else if (type === 'agent_as_judge') {
          nextCases = [{ name: 'TEST CASE 1', input: '', num_iterations: 1, threshold: 0.8, eval_type: 'agent_as_judge', additional_guidelines: '', criteria: [{ name: 'Data Completeness', description: '', max_score: 1.0 }] }];
        } else if (type === 'reliability') {
          nextCases = [{ name: 'TEST CASE 1', input: '', expected_tool_calls: [{ tool_name: '', required: true }], num_iterations: 1, threshold: 0.8, eval_type: 'reliability', additional_guidelines: '' }];
        } else if (type === 'performance') {
          nextCases = [{ name: 'TEST CASE 1', input: '', num_iterations: 1, threshold: 0.8, eval_type: 'performance', additional_guidelines: '', max_latency_ms: 200000, max_memory_mb: 512 }];
        }
      }

      setWizardForm(formPrev => ({
        ...formPrev,
        eval_type: type,
        settings,
        pass_threshold: passThreshold,
        test_case_list: nextCases
      }));

      return updatedSlots;
    });
  };

  // Criterion for Agent as Judge (Case Level)
  const addCaseCriterion = (tcIdx) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      const tc = { ...cases[tcIdx] };
      tc.criteria = [...(tc.criteria || []), { name: '', description: '', max_score: 1.0 }];
      cases[tcIdx] = tc;
      return { ...prev, test_case_list: cases };
    });
  };

  const removeCaseCriterion = (tcIdx, critIdx) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      const tc = { ...cases[tcIdx] };
      const criteria = [...(tc.criteria || [])];
      criteria.splice(critIdx, 1);
      tc.criteria = criteria;
      cases[tcIdx] = tc;
      return { ...prev, test_case_list: cases };
    });
  };

  const updateCaseCriterion = (tcIdx, critIdx, field, val) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      const tc = { ...cases[tcIdx] };
      const criteria = [...(tc.criteria || [])];
      criteria[critIdx] = { ...criteria[critIdx], [field]: val };
      tc.criteria = criteria;
      cases[tcIdx] = tc;
      return { ...prev, test_case_list: cases };
    });
  };

  // Test cases management
  const addTestCase = () => {
    const newName = `TEST CASE ${wizardForm.test_case_list.length + 1}`;
    let newCase = { 
      name: newName, 
      input: '',
      num_iterations: 1,
      threshold: 0.8,
      eval_type: wizardForm.eval_type,
      additional_guidelines: '',
      criteria: wizardForm.eval_type === 'agent_as_judge' ? [{ name: 'Data Completeness', description: '', max_score: 1.0 }] : []
    };
    if (wizardForm.eval_type === 'accuracy') {
      newCase.expected_output = '';
    } else if (wizardForm.eval_type === 'reliability') {
      newCase.expected_tool_calls = [{ tool_name: '', required: true }];
    } else if (wizardForm.eval_type === 'performance') {
      newCase.max_latency_ms = 200000;
      newCase.max_memory_mb = 512;
    }

    setWizardForm(prev => ({
      ...prev,
      test_case_list: [...prev.test_case_list, newCase]
    }));

    setExpandedStep2Cases(prev => ({
      ...prev,
      [wizardForm.test_case_list.length]: true
    }));
  };

  const removeTestCase = (idx) => {
    if (wizardForm.test_case_list.length <= 1) {
      triggerToast('At least one test case is required');
      return;
    }
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      cases.splice(idx, 1);
      return { ...prev, test_case_list: cases };
    });
  };

  const updateTestCase = (idx, field, val) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      cases[idx] = { ...cases[idx], [field]: val };
      return { ...prev, test_case_list: cases };
    });
  };

  // Reliability tool calls management
  const addToolCall = (caseIdx) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      cases[caseIdx].expected_tool_calls = [...(cases[caseIdx].expected_tool_calls || []), { tool_name: '', required: true }];
      return { ...prev, test_case_list: cases };
    });
  };

  const removeToolCall = (caseIdx, toolIdx) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      const calls = [...(cases[caseIdx].expected_tool_calls || [])];
      calls.splice(toolIdx, 1);
      cases[caseIdx].expected_tool_calls = calls;
      return { ...prev, test_case_list: cases };
    });
  };

  const updateToolCall = (caseIdx, toolIdx, field, val) => {
    setWizardForm(prev => {
      const cases = [...prev.test_case_list];
      const calls = [...(cases[caseIdx].expected_tool_calls || [])];
      calls[toolIdx] = { ...calls[toolIdx], [field]: val };
      cases[caseIdx].expected_tool_calls = calls;
      return { ...prev, test_case_list: cases };
    });
  };

  // Save pack
  const handleSaveWizard = async (status) => {
    if (!wizardForm.code.trim() || !wizardForm.name.trim()) {
      triggerToast('Code and Name are required');
      return;
    }
    
    // Simple validation of test cases
    const hasEmptyInput = wizardForm.test_case_list.some(c => !c.input.trim());
    if (hasEmptyInput) {
      triggerToast('All test cases must have an input query');
      return;
    }

    if (wizardForm.target_type === 'pipeline') {
      const hasInvalidJSON = wizardForm.test_case_list.some(c => {
        try {
          JSON.parse(c.input);
          return false;
        } catch (e) {
          return true;
        }
      });
      if (hasInvalidJSON) {
        triggerToast('All test case inputs must be valid JSON for pipeline target type');
        return;
      }
    }

    const payload = {
      ...wizardForm,
      status
    };

    try {
      if (editingPackId) {
        await updateEvalPack(payload);
        triggerToast(`Updated evaluation pack ${wizardForm.code}`);
      } else {
        await createEvalPack(payload);
        triggerToast(`Created evaluation pack ${wizardForm.code}`);
      }
      setIsWizardOpen(false);
      fetchPacksData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleWizardNext = () => {
    if (wizardStep === 1) {
      if (!wizardForm.code.trim() || !wizardForm.name.trim()) {
        triggerToast('Code and Name are required');
        return;
      }
    } else if (wizardStep === 2) {
      const hasEmptyInput = wizardForm.test_case_list.some(c => !c.input.trim());
      if (hasEmptyInput) {
        triggerToast('All test cases must have input query');
        return;
      }
      if (wizardForm.target_type === 'pipeline') {
        const hasInvalidJSON = wizardForm.test_case_list.some(c => {
          try {
            JSON.parse(c.input);
            return false;
          } catch (e) {
            return true;
          }
        });
        if (hasInvalidJSON) {
          triggerToast('All test case inputs must be valid JSON for pipeline target type');
          return;
        }
      }
    }
    setWizardStep(prev => prev + 1);
  };

  // --- RUN EXECUTION FLOW ---

  const triggerRun = async (e, pack) => {
    e.stopPropagation();
    const runId = `run_${Math.random().toString(16).slice(2, 10)}`;

    triggerToast(`Test case is running with runid: ${runId}`);

    try {
      await runEvalPack(pack.code, 'staging', 'async', runId);
    } catch (err) {
      console.error(err);
      triggerToast('Failed to trigger evaluation run');
    }
  };

  // Helper for JSON syntax coloring
  const formatJSONSyntax = (obj) => {
    const raw = JSON.stringify(obj, null, 2);
    // Escape HTML
    const escaped = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escaped.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, (match) => {
      let cls = 'number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'key';
        } else {
          cls = 'string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'boolean';
      } else if (/null/.test(match)) {
        cls = 'null';
      }
      return `<span class="${cls}">${match}</span>`;
    });
  };

  const paginatedPacks = packs;

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <PageHeaderWithInfo
          icon={faVials}
          title="Evaluation Packs"
          description="Manage model accuracy, reliability logic, response latency limits, and LLM-as-a-judge criteria evaluations. Bind evaluations to agents, planners, or workflows."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <SearchInput
              type="text"
              placeholder="Search evaluation packs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button primary onClick={initCreateWizard}>
              <Plus size={16} /> Create Evaluation Pack
            </Button>
          </div>
        </PageHeaderWithInfo>

        <FilterBar>
          <TabGroup>
            <TabButton active={activeFilterTab === 'all'} onClick={() => setActiveFilterTab('all')}>All Targets</TabButton>
            <TabButton active={activeFilterTab === 'agent'} onClick={() => setActiveFilterTab('agent')}>Agents</TabButton>
            <TabButton active={activeFilterTab === 'planner'} onClick={() => setActiveFilterTab('planner')}>Planners</TabButton>
            <TabButton active={activeFilterTab === 'pipeline'} onClick={() => setActiveFilterTab('pipeline')}>Pipelines</TabButton>
          </TabGroup>
        </FilterBar>

        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table>
            <thead>
              <tr>
                <TableHeader>Name</TableHeader>
                <TableHeader>Target</TableHeader>
                <TableHeader>Evaluation Type</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Last Modified</TableHeader>
                <TableHeader style={{ textAlign: 'right' }}>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {paginatedPacks.map(pack => {
                const iconData = getPackIcon(pack);
                return (
                  <TableRow key={pack.id} onClick={() => initEditWizard(pack)} style={{ cursor: 'pointer' }}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifycontent: 'center',
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          marginRight: '12px',
                          background: iconData.bg,
                          color: iconData.color,
                          flexShrink: 0,
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          {iconData.icon}
                        </div>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{pack.name}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          background: '#e0f2fe',
                          color: '#0369a1',
                          border: '1px solid #bae6fd',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          marginBottom: '4px'
                        }}>
                          {pack.target_type || 'Agent'}
                        </span>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                          {getTargetName(pack.target_type, pack.target_code)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {renderEvaluationTypeBadge(pack.eval_type)}
                    </TableCell>
                    <TableCell>
                      {renderStatusBadge(pack)}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.8rem', color: '#64748b' }}>
                      <div style={{ fontSize: '0.8rem', color: '#0f172a', fontWeight: 500 }}>
                        {formatLastModifiedDate(pack.updated_at)}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>
                        by {pack.updated_by || 'blue@metamorphtech.com'}
                      </div>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Button primary style={{ padding: '4px 8px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={(e) => triggerRun(e, pack)}>
                          <Play size={12} fill="white" /> Run
                        </Button>
                        <Button style={{ padding: '4px 8px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => initEditWizard(pack)}>
                          <Edit size={12} /> Edit
                        </Button>
                        <Button style={{ padding: '4px 8px', fontSize: '0.8rem', color: '#ef4444', borderColor: '#fee2e2', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={(e) => handleDelete(e, pack.id, pack.code)}>
                          <Trash2 size={12} /> Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {packs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                    No evaluation packs found matching filter.
                  </TableCell>
                </TableRow>
              )}
            </tbody>
          </Table>
          {!viewAll ? (
            <>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                setTotalPages={setTotalPages}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
              />
              {totalCount > pageSize && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '1rem' }}>
                  <Button 
                    style={{ padding: '6px 12px', fontSize: '0.8rem', background: '#f8fafc', borderColor: '#cbd5e1', color: '#475569' }}
                    onClick={() => {
                      setViewAll(true);
                      setCurrentPage(1);
                    }}
                  >
                    Show All Packs
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Showing all {totalCount} results</span>
              <Button 
                style={{ padding: '6px 12px', fontSize: '0.8rem', background: '#f8fafc', borderColor: '#cbd5e1', color: '#475569' }}
                onClick={() => {
                  setViewAll(false);
                  setCurrentPage(1);
                }}
              >
                Show Paginated
              </Button>
            </div>
          )}
        </Card>
      </motion.div>

      {/* --- WIZARD DESIGN MODAL --- */}
      <AnimatePresence>
        {isWizardOpen && (
          <WizardModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeWizardModal}
          >
            <WizardModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <WizardModalTopBar>
                <WizardModalHeader>
                  <WizardModalTitle>
                    {editingPackId ? 'Edit Evaluation Pack' : 'Design New Evaluation Pack'}
                  </WizardModalTitle>
                  <WizardCloseButton onClick={closeWizardModal}>×</WizardCloseButton>
                </WizardModalHeader>

                <WizardStepIndicator>
                  {['Target & Details', 'Configuration', 'Review & Preview'].map((stepName, idx) => (
                    <WizardStep key={idx} onClick={() => {
                      if (idx + 1 === 1) {
                        setWizardStep(1);
                      } else if (idx + 1 === 2) {
                        if (!wizardForm.code.trim() || !wizardForm.name.trim()) {
                          triggerToast('Code and Name are required');
                          return;
                        }
                        setWizardStep(2);
                      } else if (idx + 1 === 3) {
                        if (!wizardForm.code.trim() || !wizardForm.name.trim()) {
                          triggerToast('Code and Name are required');
                          return;
                        }
                        const hasEmptyInput = wizardForm.test_case_list.some(c => !c.input.trim());
                        if (hasEmptyInput) {
                          triggerToast('All test cases must have input query');
                          return;
                        }
                        if (wizardForm.target_type === 'pipeline') {
                          const hasInvalidJSON = wizardForm.test_case_list.some(c => {
                            try {
                              JSON.parse(c.input);
                              return false;
                            } catch (e) {
                              return true;
                            }
                          });
                          if (hasInvalidJSON) {
                            triggerToast('All test case inputs must be valid JSON for pipeline target type');
                            return;
                          }
                        }
                        setWizardStep(3);
                      }
                    }}>
                      <WizardStepCircle
                        active={wizardStep === idx + 1}
                        completed={wizardStep > idx + 1}
                      >
                        {wizardStep > idx + 1 ? '✓' : idx + 1}
                      </WizardStepCircle>
                      <WizardStepLabel active={wizardStep === idx + 1}>
                        {stepName}
                      </WizardStepLabel>
                    </WizardStep>
                  ))}
                </WizardStepIndicator>
              </WizardModalTopBar>

              <WizardModalSection>
                {/* STEP 1 */}
                {wizardStep === 1 && (
                  <div>
                    <InfoBox>
                      Start by defining the evaluation target and general package details. Binding this evaluation pack allows validation against specific agents, planners, or pipelines.
                    </InfoBox>
                    <FormGroup>
                      <WizardLabel>Eval Name*</WizardLabel>
                      <WizardInput 
                        type="text" 
                        value={wizardForm.name} 
                        onChange={(e) => updateWizardField('name', e.target.value)}
                        placeholder="FWA scoring accuracy test" 
                      />
                    </FormGroup>
                    <FormGroup>
                      <WizardLabel>Description (Optional)</WizardLabel>
                      <WizardTextarea 
                        value={wizardForm.description} 
                        onChange={(e) => updateWizardField('description', e.target.value)}
                        placeholder="What does this evaluation pack verify?"
                      />
                    </FormGroup>
                    <FormGroup style={{ marginTop: '1.5rem' }}>
                      <WizardLabel>Target Ecosystem Type</WizardLabel>
                      <SegmentedControl>
                        <SegmentOption active={wizardForm.target_type === 'agent'} onClick={() => setTargetType('agent')}>
                          Agent
                        </SegmentOption>
                        <SegmentOption active={wizardForm.target_type === 'planner'} onClick={() => setTargetType('planner')}>
                          Planner
                        </SegmentOption>
                        <SegmentOption active={wizardForm.target_type === 'pipeline'} onClick={() => setTargetType('pipeline')}>
                          Pipeline
                        </SegmentOption>
                      </SegmentedControl>
                    </FormGroup>
                    <FormGroup style={{ marginTop: '1.5rem' }}>
                      <WizardLabel>Evaluation Target (Specific Component)</WizardLabel>
                      <MultiSelectDropdownSearch
                        options={targets[wizardForm.target_type] || []}
                        selected={(targets[wizardForm.target_type] || []).filter(t => t.code === wizardForm.target_code)}
                        onChange={(newSel) => {
                          const code = newSel && newSel.length > 0 ? newSel[0].code : '';
                          updateWizardField('target_code', code);
                        }}
                        isSingleSelect={true}
                        placeholder="Select component..."
                        searchPlaceholder="Search options..."
                      />
                    </FormGroup>
                  </div>
                )}

                {/* STEP 2 */}
                {wizardStep === 2 && (
                  <div>
                    <InfoBox>
                      Select the evaluation methodology and define parameters. Add test cases that specify input prompts and expected outputs/behaviors to measure accuracy, reliability, or performance.
                    </InfoBox>
                    <FormGroup>
                      <WizardLabel>Evaluation Methodology</WizardLabel>
                      <SegmentedControl>
                        <SegmentOption active={wizardForm.eval_type === 'accuracy'} onClick={() => handleEvalTypeChange('accuracy')}>
                          Accuracy
                          <span className="sub">Semantic Similarity</span>
                        </SegmentOption>
                        <SegmentOption active={wizardForm.eval_type === 'agent_as_judge'} onClick={() => handleEvalTypeChange('agent_as_judge')}>
                          Agent-as-judge
                          <span className="sub">Weighted rubrics</span>
                        </SegmentOption>
                        <SegmentOption 
                          active={wizardForm.eval_type === 'reliability'} 
                          disabled={true}
                          onClick={() => {}}
                        >
                          Reliability
                          <span className="sub">Tool sequences</span>
                        </SegmentOption>
                        <SegmentOption active={wizardForm.eval_type === 'performance'} onClick={() => handleEvalTypeChange('performance')}>
                          Performance
                          <span className="sub">Latency / SLA</span>
                        </SegmentOption>
                      </SegmentedControl>
                    </FormGroup>

                    {/* Eval Settings Panel */}
                    {wizardForm.eval_type !== 'performance' && (
                      <SettingsPanel>
                        <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#334155', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Sparkles size={14} color="var(--primary-500)" /> Evaluator Parameters
                        </h4>
                        
                        {wizardForm.eval_type === 'accuracy' && (
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <FormGroup style={{ marginBottom: 0 }}>
                                <WizardLabel>Judge Model</WizardLabel>
                                <WizardSelect value={wizardForm.settings.evaluator_model} onChange={(e) => updateWizardSettings('evaluator_model', e.target.value)}>
                                  <option value="gpt-4o">Default</option>
                                  <option value="claude-sonnet-5">claude-sonnet-5</option>
                                  <option value="qwen3:8b">qwen3:8b (Local)</option>
                                </WizardSelect>
                              </FormGroup>
                              <FormGroup style={{ marginBottom: 0 }}>
                                <WizardLabel>Pass Threshold (1–10)</WizardLabel>
                                <WizardInput type="number" min="1" max="10" step="0.5" value={wizardForm.pass_threshold !== undefined && wizardForm.pass_threshold !== null ? wizardForm.pass_threshold : ''} onChange={(e) => updateWizardField('pass_threshold', parseFloat(e.target.value) || 8)} />
                              </FormGroup>
                            </div>
                          </div>
                        )}

                        {wizardForm.eval_type === 'agent_as_judge' && (
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                              <FormGroup style={{ marginBottom: 0 }}>
                                <WizardLabel>Judge Model</WizardLabel>
                                <WizardSelect value={wizardForm.settings.evaluator_model} onChange={(e) => updateWizardSettings('evaluator_model', e.target.value)}>
                                  <option value="gpt-4o">Default</option>
                                  <option value="claude-sonnet-5">claude-sonnet-5</option>
                                </WizardSelect>
                              </FormGroup>
                              <FormGroup style={{ marginBottom: 0 }}>
                                <WizardLabel>Pass Threshold (1–10)</WizardLabel>
                                <WizardInput type="number" min="1" max="10" step="0.5" value={wizardForm.pass_threshold !== undefined && wizardForm.pass_threshold !== null ? wizardForm.pass_threshold : ''} onChange={(e) => updateWizardField('pass_threshold', parseFloat(e.target.value) || 7.5)} />
                              </FormGroup>
                            </div>
                          </div>
                        )}

                        {wizardForm.eval_type === 'reliability' && (
                          <div>
                            <ToggleRow>
                              <div className="label-group">
                                <span className="title">Allow Additional Tool Calls</span>
                                <span className="desc">Do not fail if model triggers tools not strictly declared in test cases.</span>
                              </div>
                              <SwitchLabel>
                                <input type="checkbox" checked={wizardForm.settings.allow_additional_tool_calls} onChange={(e) => updateWizardSettings('allow_additional_tool_calls', e.target.checked)} />
                                <span></span>
                              </SwitchLabel>
                            </ToggleRow>
                            <ToggleRow>
                              <div className="label-group">
                                <span className="title">Enforce Order of Calls</span>
                                <span className="desc">Evaluate strict chronological sequence ordering of tool calls.</span>
                              </div>
                              <SwitchLabel>
                                <input type="checkbox" checked={wizardForm.settings.expected_call_order} onChange={(e) => updateWizardSettings('expected_call_order', e.target.checked)} />
                                <span></span>
                              </SwitchLabel>
                            </ToggleRow>
                          </div>
                        )}
                      </SettingsPanel>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>Evaluation Test Cases</h4>
                      <Button style={{ padding: '6px 12px', fontSize: '0.8rem' }} onClick={addTestCase}>
                        <Plus size={14} /> Add Test Case
                      </Button>
                    </div>

                    {/* Test Cases Cards List */}
                    {wizardForm.test_case_list.map((c, idx) => {
                      const isExpanded = !!expandedStep2Cases[idx];
                      return (
                        <TestCaseCard key={idx}>
                          <div 
                            className="header" 
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', cursor: 'pointer' }}
                            onClick={() => setExpandedStep2Cases(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          >
                            <input 
                              type="text"
                              value={c.name || `TEST CASE ${idx + 1}`}
                              onChange={(e) => updateTestCase(idx, 'name', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder={`TEST CASE ${idx + 1}`}
                              style={{ 
                                background: 'transparent',
                                border: 'none',
                                borderBottom: '1px dashed rgba(0, 102, 255, 0.3)',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                color: '#1e293b',
                                outline: 'none',
                                padding: '2px 4px',
                                width: '200px'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
                              {isExpanded ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
                              <Button 
                                style={{ padding: '3px', borderColor: 'transparent', color: '#94a3b8' }} 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeTestCase(idx);
                                }}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div style={{ marginTop: '12px' }}>
                              <FormGroup style={{ marginBottom: '10px' }}>
                                <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {wizardForm.target_type === 'pipeline' ? 'User Input*(JSON)' : 'User Input*'}
                                </WizardLabel>
                                <WizardTextarea 
                                  value={c.input} 
                                  onChange={(e) => updateTestCase(idx, 'input', e.target.value)} 
                                  placeholder={wizardForm.target_type === 'pipeline' ? 'e.g. {"key": "value"}' : 'e.g. Process underwriting details for claim CLM-991...'} 
                                  style={{ minHeight: '52px' }} 
                                />
                              </FormGroup>

                              {wizardForm.eval_type === 'performance' ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '10px' }}>
                                  <FormGroup style={{ marginBottom: 0 }}>
                                    <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Test Case Iterations*</WizardLabel>
                                    <WizardInput 
                                      type="number" 
                                      min="1"
                                      max="5"
                                      value={c.num_iterations !== undefined ? c.num_iterations : 1} 
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        updateTestCase(idx, 'num_iterations', Math.min(5, Math.max(1, val)));
                                      }} 
                                      style={{ padding: '8px', borderRadius: '6px' }}
                                    />
                                  </FormGroup>
                                  <FormGroup style={{ marginBottom: 0 }}>
                                    <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Max Latency Budget (ms)</WizardLabel>
                                    <WizardInput 
                                      type="number" 
                                      min="0"
                                      placeholder="e.g. 200000"
                                      value={c.max_latency_ms !== undefined && c.max_latency_ms !== null ? c.max_latency_ms : ''} 
                                      onChange={(e) => updateTestCase(idx, 'max_latency_ms', parseInt(e.target.value) || null)} 
                                      style={{ padding: '8px', borderRadius: '6px' }}
                                    />
                                  </FormGroup>
                                  <FormGroup style={{ marginBottom: 0 }}>
                                    <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Max Memory Limit (MB)</WizardLabel>
                                    <WizardInput 
                                      type="number" 
                                      min="0"
                                      placeholder="e.g. 512"
                                      value={c.max_memory_mb !== undefined && c.max_memory_mb !== null ? c.max_memory_mb : ''} 
                                      onChange={(e) => updateTestCase(idx, 'max_memory_mb', parseFloat(e.target.value) || null)} 
                                      style={{ padding: '8px', borderRadius: '6px' }}
                                    />
                                  </FormGroup>
                                </div>
                              ) : (wizardForm.eval_type === 'accuracy' || wizardForm.eval_type === 'agent_as_judge') ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '10px' }}>
                                  <FormGroup style={{ marginBottom: 0 }}>
                                    <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Test Case Iterations*</WizardLabel>
                                    <WizardInput 
                                      type="number" 
                                      min="1"
                                      max="5"
                                      value={c.num_iterations !== undefined ? c.num_iterations : 1} 
                                      onChange={(e) => {
                                        const val = parseInt(e.target.value) || 1;
                                        updateTestCase(idx, 'num_iterations', Math.min(5, Math.max(1, val)));
                                      }} 
                                      style={{ padding: '8px', borderRadius: '6px' }}
                                    />
                                  </FormGroup>
                                  <FormGroup style={{ marginBottom: 0 }}>
                                    <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                      {wizardForm.eval_type === 'accuracy' ? 'Similarity Threshold*' : 'Passing Threshold*'}
                                    </WizardLabel>
                                    <WizardInput 
                                      type="number" 
                                      step="0.05"
                                      min="0"
                                      max="1"
                                      value={c.threshold !== undefined ? c.threshold : (wizardForm.eval_type === 'agent_as_judge' ? 0.7 : 0.8)} 
                                      onChange={(e) => updateTestCase(idx, 'threshold', parseFloat(e.target.value) || 0.8)} 
                                      style={{ padding: '8px', borderRadius: '6px' }}
                                    />
                                  </FormGroup>
                                </div>
                              ) : (
                                <FormGroup style={{ marginBottom: '10px' }}>
                                  <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Test Case Iterations*</WizardLabel>
                                  <WizardInput 
                                    type="number" 
                                    min="1"
                                    max="5"
                                    value={c.num_iterations !== undefined ? c.num_iterations : 1} 
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value) || 1;
                                      updateTestCase(idx, 'num_iterations', Math.min(5, Math.max(1, val)));
                                    }} 
                                    style={{ padding: '8px', borderRadius: '6px', maxWidth: '200px' }}
                                  />
                                </FormGroup>
                              )}

                              {wizardForm.eval_type === 'accuracy' && (
                                <FormGroup style={{ marginBottom: 0 }}>
                                  <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Expected Outcome (Ground Truth)</WizardLabel>
                                  <WizardTextarea value={c.expected_output || ''} onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)} placeholder="Provide exact semantic expected result..." style={{ minHeight: '52px' }} />
                                </FormGroup>
                              )}

                              {wizardForm.eval_type === 'reliability' && (
                                <div style={{ marginTop: '10px' }}>
                                  <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Expected Tool Calls (In Order)</WizardLabel>
                                  {(c.expected_tool_calls || []).map((t, tcIdx) => (
                                    <ToolCallRow key={tcIdx}>
                                      <span className="index">{tcIdx + 1}.</span>
                                      <WizardInput type="text" placeholder="e.g. accident_eligibility_agent" value={t.tool_name} onChange={(e) => updateToolCall(idx, tcIdx, 'tool_name', e.target.value)} style={{ padding: '6px' }} />
                                      <SwitchLabel title="Required call" style={{ width: '36px', height: '18px' }}>
                                        <input type="checkbox" checked={t.required} onChange={(e) => updateToolCall(idx, tcIdx, 'required', e.target.checked)} />
                                        <span style={{ borderRadius: '18px' }}></span>
                                      </SwitchLabel>
                                      <Button style={{ padding: '4px', borderColor: 'transparent', color: '#94a3b8' }} onClick={() => removeToolCall(idx, tcIdx)}>
                                        <X size={12} />
                                      </Button>
                                    </ToolCallRow>
                                  ))}
                                  <Button style={{ padding: '3px 8px', fontSize: '0.75rem', marginTop: '4px' }} onClick={() => addToolCall(idx)}>
                                    + Add Tool Call
                                  </Button>
                                </div>
                              )}

                              {wizardForm.eval_type !== 'performance' && (
                                <FormGroup style={{ marginTop: '10px', marginBottom: 0 }}>
                                  <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b' }}>Additional Guidelines (Optional)</WizardLabel>
                                  <WizardTextarea 
                                    value={c.additional_guidelines || ''} 
                                    onChange={(e) => updateTestCase(idx, 'additional_guidelines', e.target.value)} 
                                    placeholder="Provide optional specific guidelines/directives for this test case..." 
                                    style={{ minHeight: '48px' }} 
                                  />
                                </FormGroup>
                              )}

                              {wizardForm.eval_type === 'agent_as_judge' && (
                                <div style={{ marginTop: '12px' }}>
                                  <WizardLabel style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Test Case Scoring Criteria Rubric</WizardLabel>
                                  {c.criteria && c.criteria.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 0.8fr 40px', gap: '6px', marginBottom: '4px', paddingLeft: '2px' }}>
                                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Criterion Name*</span>
                                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Description</span>
                                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>Max Score</span>
                                      <span></span>
                                    </div>
                                  )}
                                  {(c.criteria || []).map((crit, critIdx) => (
                                    <div key={critIdx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 0.8fr 40px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                                      <WizardInput type="text" placeholder="e.g. Tone" value={crit.name} onChange={(e) => updateCaseCriterion(idx, critIdx, 'name', e.target.value)} style={{ padding: '6px', borderRadius: '6px', fontSize: '0.8rem' }} />
                                      <WizardInput type="text" placeholder="e.g. Polite response..." value={crit.description} onChange={(e) => updateCaseCriterion(idx, critIdx, 'description', e.target.value)} style={{ padding: '6px', borderRadius: '6px', fontSize: '0.8rem' }} />
                                      <WizardInput type="number" step="0.5" placeholder="Max" value={crit.max_score} onChange={(e) => updateCaseCriterion(idx, critIdx, 'max_score', parseFloat(e.target.value) || 1.0)} style={{ padding: '6px', borderRadius: '6px', fontSize: '0.8rem' }} />
                                      <Button style={{ padding: '6px', borderColor: '#fee2e2', color: '#ef4444', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => removeCaseCriterion(idx, critIdx)}><X size={14} /></Button>
                                    </div>
                                  ))}
                                  <Button style={{ padding: '4px 10px', fontSize: '0.75rem', marginTop: '6px' }} onClick={() => addCaseCriterion(idx)}>
                                    <Plus size={12} /> Add Criterion
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </TestCaseCard>
                      );
                    })}
                  </div>
                )}

                {/* STEP 3 */}
                {wizardStep === 3 && (
                  <div style={{ color: '#0f172a' }}>
                    <InfoBox style={{ marginBottom: '1.5rem', background: '#f0f9ff', borderColor: '#bae6fd', color: '#0369a1', padding: '12px 16px', borderRadius: '8px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle size={16} /> Please review the details below before creating the evaluation pack.
                    </InfoBox>

                    {/* Summary Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' }}>
                        <div style={{ width: '40px', height: '40px', background: '#eff6ff', color: '#3b82f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <User size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Target Type</div>
                          <div style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700, textTransform: 'capitalize' }}>{wizardForm.target_type}</div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' }}>
                        <div style={{ width: '40px', height: '40px', background: '#f0fdf4', color: '#22c55e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Layout size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Test Cases</div>
                          <div style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700 }}>{wizardForm.test_case_list.length}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' }}>
                        <div style={{ width: '40px', height: '40px', background: '#eff6ff', color: '#2563eb', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <RotateCw size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pass Threshold</div>
                          <div style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700 }}>{wizardForm.pass_threshold !== undefined && wizardForm.pass_threshold !== null ? `${wizardForm.pass_threshold} / 10` : '—'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#ffffff', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' }}>
                        <div style={{ width: '40px', height: '40px', background: '#faf5ff', color: '#a855f7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Sparkles size={20} />
                        </div>
                        <div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Judge Model</div>
                          <div style={{ fontSize: '1.05rem', color: '#0f172a', fontWeight: 700 }}>{wizardForm.settings.evaluator_model === 'gpt-4o' ? 'Default' : (wizardForm.settings.evaluator_model || 'Default')}</div>
                        </div>
                      </div>
                    </div>

                    {/* Preview Cards Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '1.5rem' }}>
                      {/* CARD 1: Summary */}
                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>1. Evaluation Pack Summary</h4>
                          <Button style={{ padding: '4px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setWizardStep(1)}>
                            <Edit size={12} /> Edit
                          </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85rem' }}>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Pack Name</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{wizardForm.name}</div>
                          </div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Target Ecosystem Type</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a', textTransform: 'capitalize' }}>{wizardForm.target_type}</div>
                          </div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Evaluation Target (Component)</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{getTargetName(wizardForm.target_type, wizardForm.target_code)}</div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 2: Parameters */}
                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>2. Evaluator Parameters</h4>
                          <Button style={{ padding: '4px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setWizardStep(2)}>
                            <Edit size={12} /> Edit
                          </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.85rem' }}>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Evaluation Methodology</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>
                              {wizardForm.eval_type === 'accuracy' ? 'Accuracy (Semantic Similarity)' : (wizardForm.eval_type === 'agent_as_judge' ? 'Agent-as-judge (Weighted rubrics)' : (wizardForm.eval_type === 'reliability' ? 'Reliability (Tool sequences)' : 'Performance (Latency / SLA)'))}
                            </div>
                          </div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Judge Model</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{wizardForm.settings.evaluator_model === 'gpt-4o' ? 'Default' : (wizardForm.settings.evaluator_model || 'Default')}</div>
                          </div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ width: '180px', color: '#64748b', fontWeight: 500 }}>Pass Threshold</div>
                            <div style={{ width: '20px', color: '#64748b' }}>:</div>
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>{wizardForm.pass_threshold !== undefined && wizardForm.pass_threshold !== null ? `${wizardForm.pass_threshold} / 10` : '—'}</div>
                          </div>
                        </div>
                      </div>

                      {/* CARD 3: Test Cases */}
                      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', gridColumn: '1 / -1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>3. Test Cases ({wizardForm.test_case_list.length})</h4>
                          <Button style={{ padding: '4px 10px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setWizardStep(2)}>
                            <Plus size={12} /> Add Test Case
                          </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>
                          {wizardForm.test_case_list.map((c, idx) => {
                            const isExpanded = !!expandedPreviewCases[idx];
                            return (
                              <div key={idx} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                                <div 
                                  onClick={() => setExpandedPreviewCases(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#f8fafc', cursor: 'pointer', borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}
                                >
                                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>{c.name || `TEST CASE ${idx + 1}`}</span>
                                  {isExpanded ? <ChevronUp size={14} color="#64748b" /> : <ChevronDown size={14} color="#64748b" />}
                                </div>
                                {isExpanded && (
                                  <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem' }}>
                                    <div>
                                      <div style={{ color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>
                                        {wizardForm.target_type === 'pipeline' ? 'User Input (JSON)' : 'User Input'}
                                      </div>
                                      <div style={{ background: '#f1f5f9', padding: '8px 10px', borderRadius: '6px', color: '#1e293b', whiteSpace: 'pre-wrap', border: '1px solid #e2e8f0' }}>{c.input || '—'}</div>
                                    </div>
                                    {(wizardForm.eval_type === 'accuracy' || wizardForm.eval_type === 'agent_as_judge') && (
                                      <div style={{ display: 'flex', gap: '16px', fontSize: '0.75rem', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                        <div>
                                          <span style={{ color: '#64748b', fontWeight: 600 }}>Iterations:</span> <span style={{ color: '#1e293b', fontWeight: 700 }}>{c.num_iterations !== undefined ? c.num_iterations : 1}</span>
                                        </div>
                                        <div>
                                          <span style={{ color: '#64748b', fontWeight: 600 }}>Threshold:</span> <span style={{ color: '#1e293b', fontWeight: 700 }}>{c.threshold !== undefined ? c.threshold : (wizardForm.eval_type === 'agent_as_judge' ? 0.7 : 0.8)}</span>
                                        </div>
                                        <div>
                                          <span style={{ color: '#64748b', fontWeight: 600 }}>Type:</span> <span style={{ color: '#1e293b', fontWeight: 700, textTransform: 'capitalize' }}>{c.eval_type || wizardForm.eval_type}</span>
                                        </div>
                                      </div>
                                    )}
                                    {wizardForm.eval_type === 'performance' && (
                                      <div style={{ display: 'flex', gap: '16px', fontSize: '0.75rem', background: '#f8fafc', padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                                        <div>
                                          <span style={{ color: '#64748b', fontWeight: 600 }}>Iterations:</span> <span style={{ color: '#1e293b', fontWeight: 700 }}>{c.num_iterations !== undefined ? c.num_iterations : 1}</span>
                                        </div>
                                        {c.max_latency_ms !== undefined && c.max_latency_ms !== null && (
                                          <div>
                                            <span style={{ color: '#64748b', fontWeight: 600 }}>Max Latency:</span> <span style={{ color: '#1e293b', fontWeight: 700 }}>{c.max_latency_ms} ms</span>
                                          </div>
                                        )}
                                        {c.max_memory_mb !== undefined && c.max_memory_mb !== null && (
                                          <div>
                                            <span style={{ color: '#64748b', fontWeight: 600 }}>Max Memory:</span> <span style={{ color: '#1e293b', fontWeight: 700 }}>{c.max_memory_mb} MB</span>
                                          </div>
                                        )}
                                        <div>
                                          <span style={{ color: '#64748b', fontWeight: 600 }}>Type:</span> <span style={{ color: '#1e293b', fontWeight: 700, textTransform: 'capitalize' }}>{c.eval_type || wizardForm.eval_type}</span>
                                        </div>
                                      </div>
                                    )}
                                    {wizardForm.eval_type === 'accuracy' && (
                                      <div>
                                        <div style={{ color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Expected Outcome (Ground Truth)</div>
                                        <div style={{ background: '#f1f5f9', padding: '8px 10px', borderRadius: '6px', color: '#1e293b', whiteSpace: 'pre-wrap', border: '1px solid #e2e8f0' }}>{c.expected_output || '—'}</div>
                                      </div>
                                    )}
                                    {wizardForm.eval_type === 'reliability' && (
                                      <div>
                                        <div style={{ color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Expected Tool Calls</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          {(c.expected_tool_calls || []).map((t, tcIdx) => (
                                            <div key={tcIdx} style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between' }}>
                                              <span>{tcIdx + 1}. {t.tool_name}</span>
                                              {t.required && <span style={{ color: '#dc2626', fontWeight: 600, fontSize: '0.7rem' }}>Required</span>}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    {c.additional_guidelines && (
                                      <div style={{ marginTop: '4px' }}>
                                        <div style={{ color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Additional Guidelines</div>
                                        <div style={{ background: '#faf5ff', padding: '6px 10px', borderRadius: '6px', color: '#6b21a8', border: '1px solid #f3e8ff', fontStyle: 'italic', fontSize: '0.75rem' }}>{c.additional_guidelines}</div>
                                      </div>
                                    )}
                                    {wizardForm.eval_type === 'agent_as_judge' && c.criteria && c.criteria.length > 0 && (
                                      <div style={{ marginTop: '8px' }}>
                                        <div style={{ color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Scoring Criteria Rubric</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                          {c.criteria.map((crit, critIdx) => (
                                            <div key={critIdx} style={{ fontSize: '0.75rem', background: '#fafafb', border: '1px solid #e2e8f0', padding: '6px 10px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              <div>
                                                <span style={{ fontWeight: 700, color: '#334155' }}>{crit.name}</span>
                                                {crit.description && <span style={{ color: '#64748b', marginLeft: '6px' }}>— {crit.description}</span>}
                                              </div>
                                              <span style={{ fontWeight: 600, color: '#0f172a', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>Max Score: {crit.max_score}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* CARD 5: Validation Checklist */}
                    <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '1rem' }}>
                      <h4 style={{ margin: '0 0 16px 0', fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>5. Validation Checklist</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={16} color={wizardForm.target_code ? '#22c55e' : '#cbd5e1'} />
                          <span style={{ color: wizardForm.target_code ? '#334155' : '#94a3b8' }}>Target selected</span>
                        </div>
                        {wizardForm.eval_type !== 'agent_as_judge' && wizardForm.eval_type !== 'performance' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircle2 size={16} color={(wizardForm.test_case_list && wizardForm.test_case_list.every(c => c.expected_output && c.expected_output.trim() !== '')) ? '#22c55e' : '#cbd5e1'} />
                            <span style={{ color: (wizardForm.test_case_list && wizardForm.test_case_list.every(c => c.expected_output && c.expected_output.trim() !== '')) ? '#334155' : '#94a3b8' }}>Expected outcome provided</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={16} color={wizardForm.settings.evaluator_model ? '#22c55e' : '#cbd5e1'} />
                          <span style={{ color: wizardForm.settings.evaluator_model ? '#334155' : '#94a3b8' }}>Judge model configured</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={16} color={(wizardForm.target_code && wizardForm.settings.evaluator_model && wizardForm.test_case_list.length > 0) ? '#22c55e' : '#cbd5e1'} />
                          <span style={{ color: (wizardForm.target_code && wizardForm.settings.evaluator_model && wizardForm.test_case_list.length > 0) ? '#334155' : '#94a3b8' }}>Ready to save</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircle2 size={16} color={wizardForm.test_case_list.length > 0 ? '#22c55e' : '#cbd5e1'} />
                          <span style={{ color: wizardForm.test_case_list.length > 0 ? '#334155' : '#94a3b8' }}>At least one test case added</span>
                        </div>
                      </div>
                    </div>

                    <InfoBox style={{ background: '#f8fafc', borderColor: '#e2e8f0', color: '#64748b', padding: '12px 16px', borderRadius: '8px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle size={16} /> You can go back to edit any details. Once you are ready, create the evaluation pack.
                    </InfoBox>
                  </div>
                )}
              </WizardModalSection>

              <WizardModalFooter>
                {wizardStep === 1 ? (
                  <Button onClick={closeWizardModal}>
                    Cancel
                  </Button>
                ) : (
                  <Button onClick={() => setWizardStep(prev => prev - 1)}>
                    Back
                  </Button>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  {wizardStep === 3 && (
                    <Button onClick={() => handleSaveWizard('draft')}>
                      Save Draft
                    </Button>
                  )}
                  {wizardStep < 3 ? (
                    <Button primary onClick={handleWizardNext}>
                      Continue
                    </Button>
                  ) : (
                    <Button primary onClick={() => handleSaveWizard('active')}>
                      Create Evaluation Pack
                    </Button>
                  )}
                </div>
              </WizardModalFooter>
            </WizardModalContent>
          </WizardModal>
        )}
      </AnimatePresence>



      {/* Toast Render */}
      {toastMsg && (
        <Toast>
          <AlertCircle size={16} color="var(--primary-300)" />
          <span>{toastMsg}</span>
        </Toast>
      )}
    </PageContainer>
  );
};

export default EvalPacks;
